/**
 * Vue projet — TEXTES affichés, dans la langue de l'interface (FND-V050-087).
 *
 * `vueProjet.ts` reste la source des règles (couleur, signaux, étapes) et garde ses phrases
 * françaises pour les tests historiques et le repli. L'écran, lui, passe par ces fonctions :
 * elles recomposent les mêmes phrases à partir des MÊMES données, mais avec des clés du
 * catalogue — une interface anglaise ne montre plus « Vert : tous les signaux… ».
 *
 * Ce qui reste dans la langue de sa source : la RAISON d'un signal (message de la passerelle,
 * ou raison calculée localement en repli) — c'est une donnée, pas un libellé d'écran.
 *
 * Chaque appel est écrit `t('clé', 'texte')` avec deux littéraux : `scripts/extraire-i18n.cjs`
 * les lit syntaxiquement ; une clé calculée lui échapperait.
 */
import type { Variables } from '@/i18n/language'

import { PASTILLES, type Couleur, type EtapeJauge, type EtatEtape, type Sante, type Signal } from './vueProjet'

export type Traducteur = (cle: string, repli?: string, variables?: Variables) => string

export function libelleCouleur(c: Couleur, t: Traducteur): string {
  switch (c) {
    case 'vert': return t('vueProjet.couleurVert', 'vert')
    case 'ambre': return t('vueProjet.couleurAmbre', 'ambre')
    case 'rouge': return t('vueProjet.couleurRouge', 'rouge')
    default: return t('vueProjet.couleurSansObjet', 'sans objet')
  }
}

/** Libellé d'un signal par sa clé ; une clé inconnue garde le libellé reçu. */
export function libelleSignal(s: Pick<Signal, 'cle' | 'libelle'>, t: Traducteur): string {
  switch (s.cle) {
    case 'qualite': return t('vueProjet.signalQualite', 'Qualité')
    case 'livraison': return t('vueProjet.signalLivraison', 'Livraison')
    case 'decisions': return t('vueProjet.signalDecisions', 'Décisions')
    case 'conformite': return t('vueProjet.signalConformite', 'Conformité')
    case 'colonie': return t('vueProjet.signalColonie', 'Colonie')
    case 'exploitation': return t('vueProjet.signalExploitation', 'Exploitation')
    case 'generabilite': return t('vueProjet.signalGenerabilite', 'Générabilité')
    default: return s.libelle
  }
}

/** Phrase de la santé — même composition que `composerSante`, dans la langue de l'écran. */
export function phraseSante(s: Sante, t: Traducteur): string {
  if (s.couleur === 'sans-objet') return t('vueProjet.santeSansObjet', 'Sans objet : aucun signal n’a de donnée.')
  if (s.couleur === 'vert') return t('vueProjet.santeVert', 'Vert : tous les signaux disponibles sont verts.')
  const c = libelleCouleur(s.couleur, t)
  const titre = c.charAt(0).toUpperCase() + c.slice(1)
  const detail = s.determinants
    .map((x) => t('vueProjet.santeDeterminant', '{{signal}} : {{raison}}',
      { signal: libelleSignal(x, t).toLowerCase(), raison: x.raison }))
    .join(t('vueProjet.santeSeparateur', ' ; '))
  // `nb` et non `n` : `n` déclencherait la recherche de formes plurielles du catalogue.
  return s.determinants.length > 1
    ? t('vueProjet.santeSignaux', '{{titre}} : {{nb}} signaux — {{detail}}.', { titre, nb: s.determinants.length, detail })
    : t('vueProjet.santeUnSignal', '{{titre}} : 1 signal — {{detail}}.', { titre, detail })
}

export function libelleEtape(n: number, repli: string, t: Traducteur): string {
  switch (n) {
    case 1: return t('vueProjet.etapeCadrage', 'Cadrage')
    case 2: return t('vueProjet.etapeChoixTechno', 'Choix technologiques')
    case 3: return t('vueProjet.etapeDesign', 'Design')
    case 4: return t('vueProjet.etapeSpecification', 'Spécification')
    case 5: return t('vueProjet.etapePlanification', 'Planification')
    case 6: return t('vueProjet.etapeDeveloppement', 'Développement par les agents')
    case 7: return t('vueProjet.etapeTests', 'Tests et qualité')
    case 8: return t('vueProjet.etapeLivraison', 'Livraison')
    case 9: return t('vueProjet.etapeExploitation', 'Exploitation')
    default: return repli
  }
}

export function libelleEtat(e: EtatEtape, t: Traducteur): string {
  switch (e) {
    case 'en cours': return t('vueProjet.etatEnCours', 'en cours')
    case 'rouverte': return t('vueProjet.etatRouverte', 'rouverte')
    case 'faite': return t('vueProjet.etatFaite', 'faite')
    case 'non activée': return t('vueProjet.etatNonActivee', 'non activée')
    default: return t('vueProjet.etatAVenir', 'à venir')
  }
}

/** Phrase de la jauge — même composition que `phraseJauge`, dans la langue de l'écran. */
export function phraseJaugeTraduite(etapes: readonly EtapeJauge[], total: number, t: Traducteur): string {
  const nom = (e: EtapeJauge) => `${PASTILLES[e.n - 1]} ${libelleEtape(e.n, e.libelle, t)}`
  if (total === 0) return t('vueProjet.jaugeAucune', 'Aucune demande : toutes les étapes sont à venir.')
  const enCours = etapes.filter((e) => e.etat === 'en cours' || e.etat === 'rouverte').map((e, i) => {
    const el = i > 0
      ? t('vueProjet.jaugeElement', '{{etape}} ({{nb}})', { etape: nom(e), nb: e.demandes })
      : e.demandes > 1
        ? t('vueProjet.jaugeElementPremier', '{{etape}} ({{nb}} demandes)', { etape: nom(e), nb: e.demandes })
        : t('vueProjet.jaugeElementPremierUn', '{{etape}} ({{nb}} demande)', { etape: nom(e), nb: e.demandes })
    return e.etat === 'rouverte' ? t('vueProjet.jaugeElementRouverte', '{{element}} rouverte', { element: el }) : el
  })
  const nonActivees = etapes.filter((e) => e.etat === 'non activée').map(nom)
  const liste = enCours.join(', ')
  return nonActivees.length > 0
    ? t('vueProjet.jaugeEnCoursNonActivees', 'En cours : {{liste}} ; non activée : {{inactives}}.', { liste, inactives: nonActivees.join(', ') })
    : t('vueProjet.jaugeEnCours', 'En cours : {{liste}}.', { liste })
}

/** Sous-titre d'étape de la barre latérale (« ① Cadrage », `nav.ts`) dans la langue de l'écran. */
export function libelleSousTitreEtape(etape: string, t: Traducteur): string {
  const i = PASTILLES.findIndex((p) => etape.startsWith(p))
  const pastille = PASTILLES[i]
  if (pastille === undefined) return etape
  return `${pastille} ${libelleEtape(i + 1, etape.slice(pastille.length).trim(), t)}`
}
